import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, Plus, Minus, BarChart3, Shield, Layers, FileText,
  Cpu, Zap, Target, Activity, Package, ArrowRight, Search,
  ClipboardCheck, Monitor, Globe
} from "lucide-react";
import EvaloreWeighingCanvas from "../components/EvaloreWeighingCanvas";

/* ───── CountUp ───── */
function CountUp({ end, prefix = "", suffix = "", duration = 1600, decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / duration);
          setVal(end * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const d = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <span ref={ref}>{prefix}{d}{suffix}</span>;
}

/* ═══════ DATA ═══════ */
const STAGES = [
  { n: 1, name: "Scope & Weighting", time: "10-15 min" },
  { n: 2, name: "Vendor Scoring", time: "45-75 min" },
  { n: 3, name: "TCO Modeling", time: "15-20 min" },
  { n: 4, name: "AI Report", time: "3-5 min" },
];

const FRAMEWORKS = [
  { slug: "cpct", name: "Capital Projects Control Tower", criteria: 240, time: "90-120 min", vendors: "legacy schedulers · SYNCHRO · InEight · Ecosys · Panorix + 7 more", Icon: Monitor },
  { slug: "edms", name: "Engineering Document Management", criteria: 180, time: "60-90 min", vendors: "engineering-document tools · SDx · 3D plant-design suites NET · Docuweave · Documentum + 5", Icon: FileText },
  { slug: "procurement", name: "Procurement & Contracts", criteria: 200, time: "75-90 min", vendors: "procurement suites · spend-management suites · Jaggaer · GEP · ProcureX + 4 more", Icon: Package },
  { slug: "3d-bim", name: "3D/BIM & Engineering Design", criteria: 210, time: "75-90 min", vendors: "E3D · plant-engineering platforms · Smart 3D · Plant 3D · Formigen + 4", Icon: Layers },
  { slug: "erp", name: "ERP (Capex-Intensive)", criteria: 250, time: "90-120 min", vendors: "enterprise ERP S/4 · enterprise ERP Fusion · Dynamics 365 · Infor · IFS + 2", Icon: Globe },
  { slug: "scheduling", name: "Scheduling & Project Controls", criteria: 170, time: "60-75 min", vendors: "legacy schedulers · legacy schedulers · Asta · Deltek · nPlan · Panorix + 3", Icon: Activity },
  { slug: "hse", name: "HSE Management", criteria: 140, time: "45-60 min", vendors: "Enablon · Intelex · Cority · VelocityEHS · Sphera + 4", Icon: Shield },
  { slug: "ai-platform", name: "AI / GenAI Platform (Enterprise)", criteria: 160, time: "60-75 min", vendors: "Claude · GPT-4 · Copilot · Gemini · Cognara + 3", Icon: Cpu },
];

const FAQS = [
  { q: "How is Evalore different from Gartner Magic Quadrant or Forrester Wave?", a: "Gartner/Forrester provide industry-level positioning — who are the Leaders in a category. Evalore provides organization-specific evaluation — given YOUR weights, YOUR scope, which vendor is right for YOU? Gartner reports cost $500-5,000. Evalore lets you run the evaluation yourself for ₹25,000." },
  { q: "How is Evalore different from hiring a consulting firm?", a: "Consulting engagements cost ₹8-25 Lakh and take 8-12 weeks. Evalore produces 80% of that rigor for 2% of the cost in 0.1% of the time. We recommend consulting for high-stakes, novel, or judgment-heavy evaluations." },
  { q: "Can I trust a 3-hour evaluation for a ₹50 Cr decision?", a: "Evalore uses Gartner MQ and Forrester Wave methodology — same frameworks analysts use. The rigor is in criterion structure, reference descriptions, and weighted scoring. You still need POCs, reference calls, and contract negotiation. Evalore gets you to the shortlist faster." },
  { q: "What if my evaluation needs criteria Evalore doesn't have?", a: "Enterprise tier allows custom criteria. Starter/Professional tiers support free-form notes against existing criteria. For fundamental gaps, contact us for framework extension." },
  { q: "How does the AI report avoid hallucination?", a: "The report synthesizes YOUR scored data — structured inputs (scores, evidence, TCO). Vendor commentary references YOUR evidence plus curated vendor library. Minimal room for hallucination because it's synthesis, not generation." },
  { q: "Can I evaluate vendors not in the library?", a: "Yes. Add custom vendors at evaluation start. The library is a convenience (pre-populated from Gartner MQ and real deployment data). Enterprise tier allows permanent custom-vendor additions." },
  { q: "Who typically runs a Evalore evaluation?", a: "Enterprise Architects (formal RFP), CIO/CDO direct reports (pre-RFP landscape), Project Controls Leads (project-specific platforms), Category Managers, Strategy Consultants. Multi-user collaboration supported." },
  { q: "What about data sensitivity?", a: "Three tiers: shared Azure (ISO 27001), dedicated Azure with India/EU data residency (Enterprise), on-premise for Defense/Nuclear/PSU (Enterprise custom)." },
  { q: "How often is the vendor library updated?", a: "Quarterly for major updates (new vendors, Gartner changes, pricing). Continuously for minor updates (version releases, customer corrections). Enterprise gets early access." },
  { q: "Do we own the evaluation data?", a: "Yes, completely. Evalore retains anonymized aggregate data for platform improvement but never shares your organization-specific evaluations. Full export and permanent deletion available on request." },
];

/* ═══════ PAGE ═══════ */
export default function PlatformNirnaya() {
  return (
    <>
      <Section1Hero />
      <Section2Problem />
      <Section3Method />
      <Section4Cockpit />
      <Section5ReportEngine />
      <Section6Frameworks />
      <Section7Industries />
      <Section8Outcomes />
      <Section9Faq />
      <Section10Cta />
    </>
  );
}

/* ═══════ S1 HERO — grantha-hero-grid ═══════ */
function Section1Hero() {
  return (
    <section className="grantha-hero" data-testid="nr-hero">
      <div className="container grantha-hero-grid">
        <div className="grantha-hero-copy">
          <Link to="/platforms" className="detail-back" data-testid="nr-back">
            <ChevronLeft size={14} /> All platforms
          </Link>
          <div className="grantha-crumb mono">Platforms / AI-Powered Consulting / EVALORE</div>
          <div className="eyebrow" style={{ marginTop: 10 }}>Platform Evaluation &amp; Vendor Selection</div>
          <h1 className="grantha-h1" data-testid="nr-h1">
            <span className="grantha-indo nr-logo-lockup">
              <span className="nr-icon"><span /><span /><span className="nr-spark" /><span /><span /><span /><span /><span /><span /></span>
              <span className="nr-wordmark">Evalore</span>
            </span>
            <span className="grantha-h1-en">Choose the right tech stack. <em>Before the tech stack chooses you.</em></span>
          </h1>
          <p className="grantha-sub" data-testid="nr-hero-sub">
            Evalore evaluates enterprise platforms against 200+ weighted criteria, benchmarks candidate vendors, models 5-year TCO, and generates your integration architecture — in hours, not months. Eight frameworks. Fifty-plus vendors. One unambiguous shortlist.
          </p>
          <div className="grantha-ctas">
            <a href="https://evalore.capexara.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="nr-cta-access">Access Evalore <span className="arrow">→</span></a>
            <Link to="/contact?p=evalore" className="btn btn-ghost" data-testid="nr-cta-demo">Request Demo <span className="arrow">→</span></Link>
            <a href="#nr-method" className="btn btn-ghost">See the methodology</a>
          </div>
          <div className="grantha-trust mono">
            Gartner-Forrester-grade methodology · Self-service · Free first evaluation · ₹25,000 vs ₹8-25 Lakh consulting firm
          </div>
        </div>
        <div className="grantha-hero-stage" data-testid="nr-hero-viz">
          <EvaloreWeighingCanvas />
        </div>
      </div>
    </section>
  );
}

/* Magic Quadrant SVG */
function MagicQuadrantViz() {
  const vendors = [
    { x: 380, y: 140, r: 22, label: "VENDOR A", q: "Leader" },
    { x: 420, y: 100, r: 28, label: "VENDOR B", q: "Leader", recommended: true },
    { x: 160, y: 130, r: 20, label: "VENDOR C", q: "Challenger" },
    { x: 400, y: 370, r: 16, label: "VENDOR D", q: "Visionary" },
    { x: 130, y: 400, r: 14, label: "VENDOR E", q: "Niche" },
    { x: 350, y: 350, r: 20, label: "VENDOR F", q: "Visionary" },
  ];
  return (
    <svg viewBox="0 0 560 560" className="grantha-vault" aria-label="Magic Quadrant-style vendor evaluation chart">
      <defs>
        <linearGradient id="nrGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2E70AD" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#1E4E8C" stopOpacity="0.08" />
        </linearGradient>
      </defs>
      {/* Quadrant grid */}
      <rect x="60" y="50" width="440" height="440" fill="url(#nrGrad)" rx="8" stroke="#2E70AD" strokeOpacity="0.2" strokeWidth="1" />
      <line x1="280" y1="50" x2="280" y2="490" stroke="#5790C9" strokeWidth="1" strokeOpacity="0.4" />
      <line x1="60" y1="270" x2="500" y2="270" stroke="#5790C9" strokeWidth="1" strokeOpacity="0.4" />
      {/* Quadrant labels */}
      <text x="170" y="80" textAnchor="middle" fontSize="10" fill="#9AA2B1" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="600">CHALLENGERS</text>
      <text x="390" y="80" textAnchor="middle" fontSize="10" fill="#9AA2B1" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="600">LEADERS</text>
      <text x="170" y="475" textAnchor="middle" fontSize="10" fill="#9AA2B1" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="600">NICHE PLAYERS</text>
      <text x="390" y="475" textAnchor="middle" fontSize="10" fill="#9AA2B1" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="600">VISIONARIES</text>
      {/* Axes */}
      <text x="280" y="515" textAnchor="middle" fontSize="9" fill="#7A8599" fontFamily="var(--f-mono)" letterSpacing="1">COMPLETENESS OF VISION &rarr;</text>
      <text x="30" y="270" textAnchor="middle" fontSize="9" fill="#7A8599" fontFamily="var(--f-mono)" letterSpacing="1" transform="rotate(-90 30 270)">ABILITY TO EXECUTE &rarr;</text>

      {/* Vendor bubbles */}
      {vendors.map((v, i) => (
        <g key={i} style={{ animation: `vaultNode 4s ease-in-out ${i * 0.15}s infinite` }}>
          <circle cx={v.x} cy={v.y} r={v.r} fill="#2E70AD" fillOpacity={v.recommended ? 0.45 : 0.3} stroke="#5790C9" strokeWidth="2" strokeOpacity={0.8} />
          {v.recommended && <circle cx={v.x} cy={v.y} r={v.r + 6} fill="none" stroke="#F26B1F" strokeWidth="2.5" strokeDasharray="4 3">
            <animate attributeName="stroke-dashoffset" values="0;14" dur="3s" repeatCount="indefinite" />
          </circle>}
          <text x={v.x} y={v.y + v.r + 14} textAnchor="middle" fontSize="8" fill={v.recommended ? "#F26B1F" : "#C2D5EE"} fontFamily="var(--f-mono)" fontWeight={v.recommended ? "700" : "500"} letterSpacing="1">{v.label}</text>
        </g>
      ))}

      {/* Floating panels */}
      <g style={{ animation: "vaultFloat 6s ease-in-out infinite" }}>
        <rect x="350" y="180" width="145" height="55" rx="6" fill="#111827" fillOpacity="0.9" stroke="#2E70AD" strokeOpacity="0.5" strokeWidth="1" />
        <text x="362" y="200" fontSize="7" fill="#5790C9" fontFamily="var(--f-mono)" fontWeight="700">6 VENDORS &middot; 240 CRITERIA</text>
        <text x="362" y="214" fontSize="6.5" fill="#C2D5EE" fontFamily="var(--f-mono)">Scores: 87 &middot; 82 &middot; 76 &middot; 71 &middot; 64 &middot; 58</text>
        <text x="362" y="226" fontSize="6.5" fill="#C2D5EE" fontFamily="var(--f-mono)">5Y TCO: &#x20B9;119-162 Cr range</text>
      </g>
      <g style={{ animation: "vaultFloat 5s ease-in-out 1s infinite" }}>
        <rect x="70" y="200" width="140" height="42" rx="6" fill="#111827" fillOpacity="0.9" stroke="#F26B1F" strokeOpacity="0.4" strokeWidth="1" />
        <text x="82" y="218" fontSize="7" fill="#F26B1F" fontFamily="var(--f-mono)" fontWeight="700">AI INSIGHT</text>
        <text x="82" y="232" fontSize="6.5" fill="#C2D5EE" fontFamily="var(--f-mono)">Vendor B: 12 integration gaps</text>
      </g>
    </svg>
  );
}

/* ═══════ S2 PROBLEM — grantha-problem ═══════ */
function Section2Problem() {
  return (
    <section className="grantha-problem" data-testid="nr-problem">
      <div className="container">
        <div className="eyebrow" style={{ color: "var(--ember)" }}>The platform selection crisis</div>
        <h2 className="grantha-problem-h">
          <em>You're about to spend ₹50 Cr on a platform. You've read three analyst reports, sat through six vendor demos, and you're no closer to a decision.</em>
        </h2>
        <div className="grantha-problem-grid">
          {[
            { n: 25, prefix: "₹8–", suffix: " L", label: "typical consulting-firm platform-selection engagement cost" },
            { n: 12, suffix: " weeks", label: "typical time for a proper selection engagement" },
            { n: 73, suffix: "%", label: "of enterprise platform decisions made with incomplete evaluation" },
            { n: 500, prefix: "₹50–", suffix: " Cr", label: "typical value at stake in a single platform selection decision" },
          ].map((s, i) => (
            <div className="grantha-problem-tile" key={i} data-testid={`nr-stat-${i}`}>
              <div className="grantha-problem-num"><CountUp end={s.n} prefix={s.prefix || ""} suffix={s.suffix || ""} /></div>
              <div className="grantha-problem-label">{s.label}</div>
            </div>
          ))}
        </div>
        <p className="grantha-problem-quote">
          The cost of the wrong platform compounds for a decade. <em>Evalore makes sure you don't pick it.</em>
        </p>
      </div>
    </section>
  );
}

/* ═══════ S3 METHOD — grantha-arch (4 stages as tabs) ═══════ */
function Section3Method() {
  const [activeStage, setActiveStage] = useState(1);
  const stageDetails = {
    1: [
      { name: "Industry & project context definition", Icon: Target },
      { name: "Distribute 100% weighting across 6 meta-categories", Icon: BarChart3 },
      { name: "Flag 5-10 must-have criteria (auto-exclude on fail)", Icon: Shield },
      { name: "Select candidate vendors from pre-populated library", Icon: Search },
    ],
    2: [
      { name: "Criterion-first UX (Gartner MQ analyst pattern)", Icon: ClipboardCheck },
      { name: "Reference-described scoring: Not Supported / Basic / Best-in-Class", Icon: Target },
      { name: "Evidence capture: URLs, demo notes, screenshots per score", Icon: FileText },
      { name: "Confidence tracking: Low / Medium / High per vendor score", Icon: Shield },
    ],
    3: [
      { name: "License + Implementation + Infrastructure + Integration + Training", Icon: BarChart3 },
      { name: "Offset with productivity gains and risk-avoidance value", Icon: Activity },
      { name: "NPV at user-specified discount rate + payback period", Icon: Globe },
      { name: "Sensitivity analysis on key cost drivers", Icon: Search },
    ],
    4: [
      { name: "Magic Quadrant-style 2×2 vendor plot with shortlist", Icon: Monitor },
      { name: "40-80 page report: scorecard, heatmap, TCO, architecture", Icon: FileText },
      { name: "AI-drafted executive summary + vendor deep-dives", Icon: Cpu },
      { name: "PDF export + shareable password-protected link (30-day)", Icon: Package },
    ],
  };
  return (
    <section className="grantha-arch" id="nr-method" data-testid="nr-method">
      <div className="container">
        <div className="eyebrow">The method</div>
        <h2 className="grantha-arch-h">Four stages. Three hours. <em>One defensible decision.</em></h2>
        <p className="grantha-arch-sub">From 12 weeks to 3 hours. From ₹25 Lakh to ₹25,000. From static slide deck to living, shareable artifact.</p>
        <div className="grantha-layers" data-testid="nr-stages">
          {STAGES.map((S) => (
            <button key={S.n} type="button" className={"grantha-layer " + (activeStage === S.n ? "is-active" : "")} onMouseEnter={() => setActiveStage(S.n)} onClick={() => setActiveStage(S.n)} data-testid={`nr-stage-${S.n}`}>
              <span className="grantha-layer-n mono">STAGE · 0{S.n}</span>
              <span className="grantha-layer-name">{S.name}</span>
              <span className="grantha-layer-count mono">{S.time}</span>
            </button>
          ))}
        </div>
        <div className="grantha-modules" data-testid="nr-stage-detail">
          {stageDetails[activeStage]?.map((d, i) => (
            <div key={i} className="grantha-module" data-testid={`nr-step-${activeStage}-${i}`}>
              <div className="grantha-module-icon"><d.Icon size={22} /></div>
              <div className="grantha-module-body">
                <div className="grantha-module-name">{d.name}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S4 COCKPIT — grantha-viewer-grid ═══════ */
function Section4Cockpit() {
  return (
    <section className="grantha-viewer" data-testid="nr-cockpit">
      <div className="container grantha-viewer-grid">
        <div className="grantha-viewer-visual">
          <div className="grantha-viewer-frame">
            <div className="grantha-viewer-bar">
              <span className="mono">Evalore · CPCT Selection · ₹4,200 Cr Refinery</span>
              <span className="mono" style={{ color: "#1E4E8C" }}>147/240 scored · 73%</span>
            </div>
            <svg viewBox="0 0 420 290" className="grantha-viewer-svg">
              <rect width="420" height="290" fill="var(--bg-sunk)" />
              {/* Sidebar */}
              <rect x="0" y="0" width="100" height="290" fill="var(--bg-elev)" stroke="var(--border)" strokeWidth="0.5" />
              <text x="10" y="18" fontSize="6" fill="var(--text-3)" fontFamily="var(--f-mono)">META-CATEGORIES</text>
              {["Functional (32/40)", "Technical (12/30)", "Implementation (0/15)", "TCO (—)", "Viability (—)", "Security (—)"].map((cat, i) => (
                <text key={i} x="10" y={36 + i * 16} fontSize="6" fill={i < 2 ? "var(--text)" : "var(--text-3)"} fontFamily="var(--f-mono)">{i < 2 ? "✓ " : "○ "}{cat}</text>
              ))}
              {/* Criterion header */}
              <text x="115" y="18" fontSize="7" fill="var(--steel)" fontFamily="var(--f-mono)" fontWeight="700">CPCT-024 · Cost Control & EVM · Weight: 3/3</text>
              <text x="115" y="34" fontSize="6" fill="var(--text)" fontFamily="var(--f-sans)">"Does the platform link cost data to schedule activities</text>
              <text x="115" y="44" fontSize="6" fill="var(--text)" fontFamily="var(--f-sans)">for time-phased cost forecasting?"</text>
              {/* Reference bands */}
              <rect x="115" y="52" width="290" height="14" rx="2" fill="rgba(30,78,140,0.04)" stroke="var(--border)" strokeWidth="0.3" />
              <text x="120" y="62" fontSize="5" fill="var(--text-3)" fontFamily="var(--f-mono)">1 Not Supported | 3 Basic Support | 5 Best-in-Class</text>
              {/* Scoring rows */}
              {[
                { name: "Legacy scheduler", score: 4, conf: "HIGH" },
                { name: "SYNCHRO 4D", score: 2, conf: "MED" },
                { name: "InEight", score: 5, conf: "HIGH" },
                { name: "Ecosys", score: 3, conf: "LOW" },
                { name: "Panorix (Capexara)", score: 5, conf: "HIGH" },
                { name: "Aconex", score: 2, conf: "MED" },
              ].map((v, i) => (
                <g key={i}>
                  <rect x="115" y={72 + i * 28} width="290" height="24" rx="2" fill="none" stroke="var(--border)" strokeWidth="0.3" />
                  <text x="120" y={87 + i * 28} fontSize="6" fill="var(--text)" fontFamily="var(--f-mono)">{v.name}</text>
                  {[1, 2, 3, 4, 5].map(n => (
                    <circle key={n} cx={240 + n * 18} cy={84 + i * 28} r="4" fill={n === v.score ? "#1E4E8C" : "none"} stroke={n === v.score ? "#1E4E8C" : "var(--border)"} strokeWidth="1" />
                  ))}
                  <text x="350" y={87 + i * 28} fontSize="5" fill={v.conf === "HIGH" ? "#10B981" : v.conf === "LOW" ? "var(--text-3)" : "#1E4E8C"} fontFamily="var(--f-mono)">{v.conf}</text>
                </g>
              ))}
              {/* Bottom nav */}
              <text x="115" y="280" fontSize="6" fill="var(--text-3)" fontFamily="var(--f-mono)">← Prev   |   Skip   |   Next →     Auto-saved 12s ago</text>
            </svg>
            <div className="grantha-viewer-meta mono">
              <div>Criterion-First Mode · 6 vendors · 240 criteria</div>
              <div>Auto-save every input · Resume anytime</div>
            </div>
          </div>
        </div>
        <div className="grantha-viewer-copy">
          <div className="eyebrow">The scoring cockpit</div>
          <h2 className="grantha-viewer-h"><em>Score like a Gartner analyst.</em></h2>
          <div className="grantha-features">
            {[
              { n: "01", t: "Criterion-first UX", b: "Score all vendors on one criterion at a time — the Gartner MQ analyst pattern. Toggle to vendor-first if preferred." },
              { n: "02", t: "Reference-described scoring", b: "Every criterion has three reference descriptions (Not Supported / Basic / Best-in-Class). Eliminates scoring inconsistency." },
              { n: "03", t: "Evidence-backed", b: "Attach URLs, demo notes, screenshots to every score. Defensible in procurement board reviews." },
              { n: "04", t: "Confidence-tracked", b: "Flag Low/Medium/High confidence per score. Low-confidence items queued for vendor follow-up." },
              { n: "05", t: "Multi-user collaboration", b: "Invite teammates to score in parallel. Reconcile divergences in a review pass." },
              { n: "06", t: "Must-have compliance check", b: "Vendors failing any must-have criterion auto-excluded with full audit trail." },
            ].map(f => (
              <div className="grantha-feature" key={f.n}>
                <span className="grantha-feature-n mono">{f.n}</span>
                <div><div className="grantha-feature-t">{f.t}</div><div className="grantha-feature-b">{f.b}</div></div>
              </div>
            ))}
          </div>
          <blockquote className="grantha-pull">The best evaluations aren't faster. <em>They're more defensible.</em></blockquote>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S5 AI REPORT — grantha-wf dark band ═══════ */

/* Mini-card for the Report Engine visual: renders 160×128 card with a tiny chart */
function MiniCard({ x, y, label, type }) {
  const W = 160;
  const H = 128;
  const delay = { quadrant: 0, heatmap: 0.35, bars: 0.7, matrix: 1.05, arch: 1.4, checklist: 1.75 }[type] || 0;

  return (
    <g transform={`translate(${x}, ${y})`} style={{ animation: `fadeInSoft .7s cubic-bezier(.2,.8,.2,1) ${delay}s both` }}>
      <rect x="0" y="0" width={W} height={H} rx="6" fill="#0F1628" stroke="rgba(30,78,140,0.45)" strokeWidth="0.8" />
      <rect x="0" y="0" width={W} height="22" rx="6" fill="#050810" />
      <text x="10" y="15" fontSize="7" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.6" fontWeight="700">{label}</text>
      <text x={W - 10} y="15" textAnchor="end" fontSize="7" fill="rgba(243,244,246,0.4)" fontFamily="var(--f-mono)">▣</text>

      {type === "quadrant" && (
        <g transform="translate(20, 36)">
          <rect x="0" y="0" width="120" height="76" fill="none" stroke="rgba(87,144,201,0.25)" strokeWidth="0.6" />
          <line x1="60" y1="0" x2="60" y2="76" stroke="rgba(87,144,201,0.35)" strokeWidth="0.6" />
          <line x1="0" y1="38" x2="120" y2="38" stroke="rgba(87,144,201,0.35)" strokeWidth="0.6" />
          <text x="62" y="8" fontSize="5" fill="rgba(243,244,246,0.4)" fontFamily="var(--f-mono)">LEADERS</text>
          <circle cx="30" cy="58" r="3" fill="#5790C9" />
          <circle cx="42" cy="44" r="3" fill="#5790C9" />
          <circle cx="68" cy="28" r="3" fill="#5790C9" />
          <circle cx="88" cy="18" r="4.5" fill="#F26B1F" style={{ animation: "pulseDot 2.2s ease-in-out infinite" }} />
          <circle cx="74" cy="52" r="3" fill="#5790C9" />
          <circle cx="20" cy="36" r="3" fill="#5790C9" />
          <text x="96" y="12" fontSize="6" fill="#F26B1F" fontFamily="var(--f-mono)" fontWeight="700">V3</text>
        </g>
      )}

      {type === "heatmap" && (
        <g transform="translate(20, 34)">
          {[0, 1, 2, 3, 4, 5].map((r) => (
            [0, 1, 2, 3, 4].map((c) => {
              const v = Math.abs(Math.sin((r + 1) * (c + 1) * 0.7));
              const isWinner = c === 2 && r === 1;
              const fill = isWinner ? "#F26B1F" : `rgba(87,144,201,${0.2 + v * 0.55})`;
              return <rect key={`${r}-${c}`} x={c * 22} y={r * 13} width="20" height="11" rx="1.2" fill={fill} />;
            })
          ))}
          <text x="60" y="94" textAnchor="middle" fontSize="5.5" fill="rgba(243,244,246,0.4)" fontFamily="var(--f-mono)">6 META · 5 VENDORS</text>
        </g>
      )}

      {type === "bars" && (
        <g transform="translate(20, 34)">
          {[
            { l: "V1", w: 58, v: "₹98" },
            { l: "V2", w: 76, v: "₹124" },
            { l: "V3", w: 96, v: "₹152" },
            { l: "V4", w: 70, v: "₹114" },
            { l: "V5", w: 62, v: "₹102" },
          ].map((b, i) => (
            <g key={b.l} transform={`translate(0, ${i * 14})`}>
              <text x="0" y="8" fontSize="6" fill="rgba(243,244,246,0.55)" fontFamily="var(--f-mono)">{b.l}</text>
              <rect x="14" y="2" width="110" height="8" rx="1.2" fill="rgba(30,78,140,0.25)" />
              <rect x="14" y="2" width={b.w} height="8" rx="1.2" fill={i === 2 ? "#F26B1F" : "#5790C9"} />
              <text x="126" y="8" fontSize="6" fill="rgba(243,244,246,0.6)" fontFamily="var(--f-mono)">{b.v}</text>
            </g>
          ))}
          <text x="60" y="92" textAnchor="middle" fontSize="5.5" fill="rgba(243,244,246,0.4)" fontFamily="var(--f-mono)">₹ Cr · 5-YR TCO</text>
        </g>
      )}

      {type === "matrix" && (
        <g transform="translate(28, 34)">
          {[0, 1, 2].map((r) => (
            [0, 1, 2].map((c) => {
              const sev = r + c;
              const color = sev <= 1 ? "rgba(87,144,201,0.45)" : sev === 2 ? "rgba(106,182,230,0.55)" : "rgba(242,107,31,0.6)";
              return <rect key={`${r}-${c}`} x={c * 30} y={r * 22} width="28" height="20" rx="2" fill={color} />;
            })
          ))}
          <circle cx="74" cy="8" r="2.2" fill="#F26B1F" />
          <circle cx="44" cy="30" r="2.2" fill="#F26B1F" />
          <text x="4" y="82" fontSize="5.5" fill="rgba(243,244,246,0.4)" fontFamily="var(--f-mono)">LIKELIHOOD × IMPACT</text>
        </g>
      )}

      {type === "arch" && (
        <g transform="translate(16, 38)">
          <rect x="0"  y="14" width="28" height="20" rx="2" fill="none" stroke="#5790C9" strokeWidth="0.8" />
          <rect x="50" y="6"  width="28" height="20" rx="2" fill="none" stroke="#5790C9" strokeWidth="0.8" />
          <rect x="50" y="38" width="28" height="20" rx="2" fill="none" stroke="#5790C9" strokeWidth="0.8" />
          <rect x="100" y="14" width="28" height="20" rx="2" fill="none" stroke="#F26B1F" strokeWidth="1" />
          <path d="M28 24 L50 16" stroke="#5790C9" strokeWidth="0.8" strokeDasharray="2 2" />
          <path d="M28 24 L50 48" stroke="#5790C9" strokeWidth="0.8" strokeDasharray="2 2" />
          <path d="M78 16 L100 24" stroke="#F26B1F" strokeWidth="1" />
          <path d="M78 48 L100 24" stroke="#F26B1F" strokeWidth="1" />
          <text x="4" y="27" fontSize="5" fill="rgba(243,244,246,0.55)" fontFamily="var(--f-mono)">ERP</text>
          <text x="54" y="19" fontSize="5" fill="rgba(243,244,246,0.55)" fontFamily="var(--f-mono)">API</text>
          <text x="54" y="51" fontSize="5" fill="rgba(243,244,246,0.55)" fontFamily="var(--f-mono)">DCS</text>
          <text x="104" y="27" fontSize="5" fill="#F26B1F" fontFamily="var(--f-mono)" fontWeight="700">V3</text>
          <text x="60" y="84" textAnchor="middle" fontSize="5.5" fill="rgba(243,244,246,0.4)" fontFamily="var(--f-mono)">AUTO-GENERATED</text>
        </g>
      )}

      {type === "checklist" && (
        <g transform="translate(12, 34)">
          {[
            { t: "Scope & success criteria", d: true },
            { t: "Test dataset & sandbox", d: true },
            { t: "Reference-call questions", d: true },
            { t: "Integration test plan", d: false },
            { t: "Go/no-go gates", d: false },
          ].map((it, i) => (
            <g key={i} transform={`translate(0, ${i * 14})`}>
              <rect x="0" y="0" width="9" height="9" rx="1.2" fill={it.d ? "#F26B1F" : "none"} stroke={it.d ? "#F26B1F" : "#5790C9"} strokeWidth="0.8" />
              {it.d && <path d="M1.8 4.8 L3.8 6.8 L7.2 3" stroke="#0F1628" strokeWidth="1" fill="none" strokeLinecap="round" strokeLinejoin="round" />}
              <text x="14" y="8" fontSize="6" fill={it.d ? "rgba(243,244,246,0.85)" : "rgba(243,244,246,0.5)"} fontFamily="var(--f-mono)">{it.t}</text>
            </g>
          ))}
          <text x="68" y="84" textAnchor="middle" fontSize="5.5" fill="rgba(243,244,246,0.4)" fontFamily="var(--f-mono)">3 OF 5 · READY</text>
        </g>
      )}
    </g>
  );
}

function Section5ReportEngine() {
  return (
    <section className="grantha-wf" data-testid="nr-report">
      <div className="container grantha-wf-grid">
        <div>
          <div className="eyebrow" style={{ color: "var(--steel-200)" }}>The AI report engine</div>
          <h2 className="grantha-wf-h">40-80 pages. Generated in 3 minutes. <em>Defensible in any boardroom.</em></h2>
          <p className="grantha-wf-body">When you click "Generate Report", Evalore's AI engine synthesizes your weighted scores, TCO data, and qualitative evidence into a consulting-grade selection report.</p>
          <ul className="grantha-wf-list">
            <li><strong>Executive Summary</strong> — The 5-minute CxO read with top recommendation</li>
            <li><strong>Vendor Shortlist & Tiering</strong> — Magic Quadrant plot with Leader/Challenger/Niche</li>
            <li><strong>Scorecard Matrix</strong> — Weighted scores by meta-category with visual heatmap</li>
            <li><strong>TCO Analysis</strong> — 5-year breakdown, payback, NPV, sensitivity analysis</li>
            <li><strong>Integration Architecture</strong> — AI-generated diagram for top-2 vendors</li>
            <li><strong>Vendor Deep-Dives</strong> — Strengths, weaknesses, POC focus, negotiation leverage</li>
            <li><strong>Risk & Mitigation</strong> — AI-identified risks with mitigation strategies</li>
            <li><strong>Next Steps & POC Plan</strong> — Scope, success criteria, reference call questions</li>
          </ul>
        </div>
        <div className="grantha-wf-diagram">
          <svg viewBox="0 0 560 500" style={{ width: "100%", maxWidth: 560, margin: "0 auto", display: "block" }} aria-label="Evalore report engine — six mini-modules">
            <defs>
              <linearGradient id="nr-rep-bg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0B1220" />
                <stop offset="100%" stopColor="#070A12" />
              </linearGradient>
              <pattern id="nr-rep-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M20 0H0V20" fill="none" stroke="#1E4E8C" strokeOpacity="0.10" strokeWidth="0.5" />
              </pattern>
              <radialGradient id="nr-rep-core" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#F26B1F" stopOpacity="0.45" />
                <stop offset="60%" stopColor="#F26B1F" stopOpacity="0.10" />
                <stop offset="100%" stopColor="#F26B1F" stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* Canvas */}
            <rect x="0" y="0" width="560" height="500" rx="10" fill="url(#nr-rep-bg)" />
            <rect x="0" y="0" width="560" height="500" rx="10" fill="url(#nr-rep-grid)" />

            {/* Top status bar */}
            <rect x="0" y="0" width="560" height="32" fill="#050810" />
            <circle cx="18" cy="16" r="4" fill="#F26B1F" style={{ animation: "pulseDot 1.8s ease-in-out infinite" }} />
            <text x="30" y="19" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" fontWeight="700" letterSpacing="1.6">AI REPORT ENGINE · GENERATING</text>
            <text x="550" y="19" textAnchor="end" fontSize="9" fill="rgba(243,244,246,0.55)" fontFamily="var(--f-mono)" letterSpacing="1.4">47 PAGES · 8/8 SECTIONS · 2m 47s</text>

            {/* Winning score badge — top right */}
            <g transform="translate(416, 48)">
              <rect x="0" y="0" width="128" height="56" rx="6" fill="#0F1628" stroke="#F26B1F" strokeWidth="1.4" />
              <text x="12" y="16" fontSize="7" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.6" fontWeight="700">COMPOSITE FIT</text>
              <text x="12" y="40" fontSize="28" fill="#F3F4F6" fontFamily="var(--f-mono)" fontWeight="700">87</text>
              <text x="58" y="40" fontSize="11" fill="rgba(243,244,246,0.55)" fontFamily="var(--f-mono)">/100</text>
              <text x="12" y="52" fontSize="7" fill="#8BB7E0" fontFamily="var(--f-mono)" letterSpacing="1.4">RANK 1 · CONF 94%</text>
            </g>

            {/* Left meta column */}
            <g transform="translate(20, 48)">
              <text x="0" y="8" fontSize="7" fill="#5790C9" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">REPORT</text>
              <text x="0" y="26" fontSize="14" fill="#F3F4F6" fontFamily="var(--f-display)" fontWeight="600">Vendor Selection</text>
              <text x="0" y="44" fontSize="8" fill="rgba(243,244,246,0.5)" fontFamily="var(--f-mono)" letterSpacing="1.4">v1.4 · AI-augmented</text>
              <text x="0" y="70" fontSize="7" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="1.6">WINNER · V3</text>
            </g>

            {/* ─── 6 Mini-module cards ─── */}
            {/* Row 1 */}
            <MiniCard x={20}  y={126} label="MAGIC QUADRANT" type="quadrant" />
            <MiniCard x={200} y={126} label="SCORECARD HEATMAP" type="heatmap" />
            <MiniCard x={380} y={126} label="TCO · 5-YEAR" type="bars" />
            {/* Row 2 */}
            <MiniCard x={20}  y={280} label="RISK MATRIX" type="matrix" />
            <MiniCard x={200} y={280} label="INTEGRATION ARCH" type="arch" />
            <MiniCard x={380} y={280} label="POC PLAN · 8 STEPS" type="checklist" />

            {/* Ambient ember glow at hub */}
            <circle cx="280" cy="250" r="90" fill="url(#nr-rep-core)" style={{ animation: "glowPulse 3.6s ease-in-out infinite" }} />

            {/* Bottom meta ticker */}
            <g transform="translate(20, 456)">
              <rect x="0" y="0" width="520" height="28" rx="4" fill="#050810" stroke="rgba(30,78,140,0.35)" strokeWidth="0.8" />
              <text x="12" y="18" fontSize="7.5" fill="#8BB7E0" fontFamily="var(--f-mono)" letterSpacing="1.2">
                <tspan fill="#F26B1F" fontWeight="700">●</tspan>
                <tspan dx="5">6 VENDORS</tspan>
                <tspan dx="10" fill="rgba(243,244,246,0.4)">·</tspan>
                <tspan dx="10">240 CRITERIA</tspan>
                <tspan dx="10" fill="rgba(243,244,246,0.4)">·</tspan>
                <tspan dx="10">6 META</tspan>
                <tspan dx="10" fill="rgba(243,244,246,0.4)">·</tspan>
                <tspan dx="10">1,142 CITATIONS</tspan>
                <tspan dx="10" fill="rgba(243,244,246,0.4)">·</tspan>
                <tspan dx="10" fill="#F26B1F" fontWeight="700">BOARD-READY</tspan>
              </text>
            </g>
          </svg>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S6 FRAMEWORKS — grantha-integ style (2x4 grid) ═══════ */
function Section6Frameworks() {
  return (
    <section className="grantha-integ" data-testid="nr-frameworks">
      <div className="container">
        <div className="eyebrow">Eight frameworks</div>
        <h2 className="grantha-integ-h">Every major platform decision in your <em>capital-project tech stack.</em></h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
          {FRAMEWORKS.map((fw, i) => (
            <div key={fw.slug} style={{ background: "var(--bg-elev)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "20px 16px", transition: "transform 0.2s, border-color 0.2s", cursor: "pointer" }} className="grantha-module" data-testid={`nr-fw-${fw.slug}`}>
              <div className="grantha-module-icon"><fw.Icon size={22} /></div>
              <div className="grantha-module-body">
                <div className="grantha-module-name">{fw.name}</div>
                <div style={{ fontFamily: "var(--f-mono)", fontSize: 11, color: "var(--text-3)", margin: "6px 0" }}>{fw.criteria} criteria · {fw.time}</div>
                <div className="grantha-module-desc">{fw.vendors}</div>
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "var(--f-serif)", fontStyle: "italic", fontSize: 18, color: "var(--text-2)", textAlign: "center", marginTop: 40 }}>
          Eight frameworks. 1,550+ criteria. One unified evaluation methodology.
        </p>
      </div>
    </section>
  );
}

/* ═══════ S7 INDUSTRIES — grantha-inds ═══════ */
function Section7Industries() {
  return (
    <section className="grantha-inds" data-testid="nr-industries">
      <div className="container">
        <div className="eyebrow">Industries served</div>
        <h2 className="grantha-inds-h">Platform decisions across <em>every capital-project industry.</em></h2>
        <div className="grantha-chips">
          {["Oil, Gas & Energy", "Power & Utilities", "Chemicals", "Infrastructure", "Metals, Mining & Minerals", "Manufacturing", "Marine"].map(c => <Link to="/industries" className="grantha-chip" key={c}>{c}</Link>)}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S8 OUTCOMES — grantha-out ═══════ */
function Section8Outcomes() {
  return (
    <section className="grantha-out" data-testid="nr-outcomes">
      <div className="container">
        <div className="eyebrow">Measured impact</div>
        <h2 className="grantha-out-h">What enterprises measure after using <em>Evalore.</em></h2>
        <div className="grantha-out-grid">
          {[
            { big: "97%", head: "cost reduction vs consulting-firm engagement", body: "From ₹8-25 Lakh to ₹25,000 per evaluation." },
            { big: "96%", head: "time reduction", body: "From 8-12 weeks to 3-5 hours user time." },
            { big: "1,550+", head: "criteria across 8 frameworks", body: "Gartner-Forrester-grade methodology, self-service." },
          ].map((c, i) => (
            <div className="grantha-out-card" key={i} data-testid={`nr-out-${i}`}>
              <div className="grantha-out-num">{c.big}</div>
              <div className="grantha-out-head">{c.head}</div>
              <div className="grantha-out-body">{c.body}</div>
            </div>
          ))}
        </div>
        <Link to="/resources" className="grantha-out-cta">Download the Evalore methodology whitepaper <span className="arrow">→</span></Link>
      </div>
    </section>
  );
}

/* ═══════ S9 FAQ — grantha-faq ═══════ */
function Section9Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="grantha-faq" data-testid="nr-faq">
      <div className="container">
        <div className="eyebrow">Frequently asked</div>
        <h2 className="grantha-faq-h">Everything a <em>CIO or enterprise architect</em> wants to know.</h2>
        <div className="grantha-faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={"grantha-faq-item " + (isOpen ? "is-open" : "")} data-testid={`nr-faq-${i}`}>
                <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} className="grantha-faq-q" aria-expanded={isOpen}>
                  <span>{f.q}</span>
                  {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                </button>
                {isOpen && <div className="grantha-faq-a" data-testid={`nr-faq-answer-${i}`}>{f.a}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S10 CTA — grantha-cta ═══════ */
function Section10Cta() {
  return (
    <section className="grantha-cta" data-testid="nr-cta">
      <div className="container grantha-cta-inner">
        <div className="eyebrow" style={{ color: "#F26B1F" }}>Start your first evaluation — free</div>
        <h2 className="grantha-cta-h">Your first evaluation is free. <em>Your next platform decision shouldn't be a gamble.</em></h2>
        <p className="grantha-cta-sub">Every new account gets one free token — enough for a complete evaluation of any of the 8 frameworks. No credit card required. No commitment. Run your hardest platform selection through Evalore and see the output.</p>
        <div className="grantha-cta-row">
          <Link to="/contact?p=evalore" className="btn btn-primary grantha-cta-btn" data-testid="nr-cta-free">Start Free Evaluation <span className="arrow">→</span></Link>
          <Link to="/contact?p=evalore" className="btn btn-ghost grantha-cta-ghost">Book a 20-min methodology walkthrough</Link>
        </div>
      </div>
    </section>
  );
}
